import React, { useCallback, useEffect } from 'react'
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch, useWatch } from 'react-hook-form'
import { c, t } from 'ttag'
import { FormData, GoalConfig } from '../../ChallengeAdmin'
import { CustomTypeCategory, customTypes } from '../../../../services/CustomTypes'
import {
  buildDivisionCategoryLeaderboardId,
  getDivisionCategoryIdFromLeaderboardId,
  isActivityIndexType,
  isDivisionCategoryLeaderboardId,
  isSimpleDayType,
} from '../../../../common/helpers/challenges'
import { DivisionCategoryRow, ProgressType } from '../../../../services/api.types'
import { translateDivisionCategoryType } from '../../../TeamManagement/TeamManagement'
import { ActivityView } from '../../../Activity/store/ActivityReducer'
import YumAddButton from '../../../../components/YumAddButton/YumAddButton'
import YumMediumTitle from '../../../../components/YumMediumTitle/YumMediumTitle'
import TotalAmountGoal from './TotalAmountGoal'
import ConsistencyGoal from './ConsistencyGoal'
import { FormGroup, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import YumFormSwitch from '../../../../components/YumFormSwitch/YumFormSwitch'

import { ReactComponent as SimpleManualEntryInactiveIcon } from '../../../../assets/v3/icons/simple-manual-entry-inactive.svg'
import {
  getConsistencyGoalValidationRules,
  getTotalAmountGoalValidationRules,
  shouldShowDailyGoal,
} from './goalValidationHelpers'
import CustomUnitField from './fields/CustomUnitField'
import YumRemoveButton from '../../../../components/YumRemoveButton/YumRemoveButton'
import { getErrorMessage, getFieldName } from './goalFormHelpers'
import YumSelect from '../../../../components/YumSelect/YumSelect'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'
import DailyGoalField from './fields/DailyGoalField'
import { useDailyGoalEndAdornment, useDailyGoalUnit } from './goalUnitHelpers'

import { ReactComponent as CollectiveGoalInactiveIcon } from '../../../../assets/v3/icons/collective-goal-inactive.svg'
import IndexTargetField from './fields/IndexTargetField'

/**
 * Reusable component for rendering a goal section (individual, team, country, or group).
 * Handles both total amount and consistency goal types.
 * Defined outside main component to prevent recreation on every render.
 */
interface GoalSectionProps {
  title: string
  goal: GoalConfig | null | undefined
  fieldPrefix: string
  showAddButton: boolean
  onAddGoal: () => void
  goalType: string
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  activityType: CustomTypeCategory
  formStep: number
  // Whether this is an individual goal (affects consistency unit text and max validation).
  // For team, country, group, etc. goals this should be false.
  isIndividualGoal?: boolean
  isLegacyChallenge?: boolean
}

const GoalSection: React.FC<GoalSectionProps> = React.memo(
  ({
    title,
    goal,
    fieldPrefix,
    showAddButton,
    onAddGoal,
    goalType,
    control,
    watch,
    setValue,
    errors,
    activityType,
    formStep,
    isIndividualGoal = false,
    isLegacyChallenge = false,
  }) => {
    if (!showAddButton && (!goal || !goal.enabled)) {
      return null
    }

    const goalPath = fieldPrefix || 'goals.individual'
    // Use useWatch for better reactivity
    const currentGoal = useWatch({ control, name: goalPath as any })

    // Handler to remove/disable the goal
    const handleRemoveGoal = () => {
      if (currentGoal && currentGoal.enabled) {
        setValue(goalPath as any, { ...currentGoal, enabled: false })
      }
    }

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <YumMediumTitle>{title}</YumMediumTitle>
          {/* {goal && goal.enabled && (
            <YumRemoveButton
              onClick={handleRemoveGoal}
              style={{
                flexShrink: 0,
                //marginRight: '-7px',
                //padding: '8px',
                padding: '0',
                //position: 'relative',
                //top: '-5px',
                marginTop: '10px',
              }}
            />
          )} */}
        </div>

        {goal && goal.enabled && (
          <>
            {goalType !== 'consistentDays' && (
              <TotalAmountGoal
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                activityType={activityType}
                formStep={formStep}
                fieldPrefix={fieldPrefix}
                hideRemove={isLegacyChallenge}
              />
            )}
            {goalType === 'consistentDays' && (
              <ConsistencyGoal
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                activityType={activityType}
                formStep={formStep}
                fieldPrefix={fieldPrefix}
                isIndividualGoal={isIndividualGoal}
                hideRemove={isLegacyChallenge}
              />
            )}
          </>
        )}

        {showAddButton && (!goal || !goal.enabled) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <YumAddButton label={t`Add goal`} onClick={onAddGoal} />
          </div>
        )}
      </>
    )
  },
)

interface ChallengeStepGoalProps {
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  activityType: CustomTypeCategory
  formStep: number
  divisionCategories: DivisionCategoryRow[]
  isLegacyChallenge: boolean
}

/**
 * Component for managing challenge step goals.
 * Handles individual, team, country, group, and collective goals.
 * Supports both total amount and consistency goal types.
 */
export default function ChallengeStepGoal(props: ChallengeStepGoalProps) {
  const { control, watch, setValue, errors, activityType, formStep, divisionCategories, isLegacyChallenge } = props

  // Watch form values
  const goalType = watch('goalType')
  // Ordered list of enabled leaderboards from the form.
  let leaderboards = watch('leaderboards') || []
  const goalsConfig = watch('goals')

  // Extract individual goal configs
  const teamGoal = goalsConfig?.team
  const countryGoal = goalsConfig?.country
  const groupGoals = goalsConfig?.group || {}
  const individualGoal = goalsConfig?.individual
  const collectiveGoal = goalsConfig?.collective

  // Helper constants
  const isCalories = activityType === CustomTypeCategory.CALORIES
  const isActivityIndex = isActivityIndexType(activityType as any as ProgressType)

  // Activity index aggregation must always be AVG, so keep it forced and read-only.
  useEffect(() => {
    if (isActivityIndex) {
      setValue('avgAgg', true, { shouldValidate: true, shouldDirty: false })
    }
  }, [isActivityIndex, setValue])

  // Helper function to create a default goal configuration
  const createDefaultGoalConfig = (): GoalConfig => ({
    enabled: true,
    totalGoal: '',
    //dailyGoal: '',
    persistenceGoal: '',
    indexTarget: '',
  })

  // ============================================================================
  // Goal Handlers
  // ============================================================================

  /**
   * Generic handler for adding a goal.
   * Sets the goal's enabled property to true.
   * Memoized to prevent GoalSection re-renders.
   */
  const handleAddGoal = useCallback(
    (goalPath: string) => {
      const currentGoal = watch(goalPath as any)
      if (!currentGoal || !currentGoal.enabled) {
        const newGoal = currentGoal ? { ...currentGoal, enabled: true } : createDefaultGoalConfig()
        setValue(goalPath as any, newGoal)
      }
    },
    [watch, setValue],
  )

  // Specific goal handlers - memoized to prevent GoalSection re-renders
  const handleAddTeamGoal = useCallback(() => handleAddGoal('goals.team'), [handleAddGoal])
  const handleAddCountryGoal = useCallback(() => handleAddGoal('goals.country'), [handleAddGoal])
  const handleAddCollectiveGoal = useCallback(() => handleAddGoal('goals.collective'), [handleAddGoal])
  const handleAddIndividualGoal = useCallback(() => handleAddGoal('goals.individual'), [handleAddGoal])
  const handleAddGroupGoal = useCallback(
    (categoryId: string) => handleAddGoal(`goals.group.${categoryId}`),
    [handleAddGoal],
  )

  // Simple manual entry field toggle
  const simpleManualEntry = watch('simpleManualEntry')
  const customUnit = watch('customUnit') ?? ''
  const customType = watch('customType')
  const simpleManualEntryField = (
    <FormGroup>
      <ListItem
        //style={{ backgroundColor: 'white' }}
        role={undefined}
        dense
        button
        onClick={() =>
          //setValue((fieldPrefix ? `${fieldPrefix}.simpleManualEntry` : 'simpleManualEntry') as any, !simpleManualEntry)
          setValue('simpleManualEntry' as any, !simpleManualEntry)
        }
      >
        <ListItemIcon>
          <SimpleManualEntryInactiveIcon />
        </ListItemIcon>
        <ListItemText primary={t`One-click manual entry`} />
        <ListItemIcon>
          <YumFormSwitch control={control} name="simpleManualEntry" />
        </ListItemIcon>
      </ListItem>
    </FormGroup>
  )

  const isDefinedCustomActivity = customTypes.some((ct) => ct.id === activityType)

  // Get validation rules for all fields
  const validationRules = getTotalAmountGoalValidationRules(
    formStep,
    activityType,
    goalType,
    isDefinedCustomActivity,
    simpleManualEntry,
  )
  const consistencyValidationRules = getConsistencyGoalValidationRules(
    formStep,
    activityType,
    goalType,
    isDefinedCustomActivity,
    simpleManualEntry,
  )

  const showCustomUnitField = isDefinedCustomActivity && activityType !== CustomTypeCategory.POINTS
  // Only show the aggregation method when a group-based leaderboard is selected.
  // Individual-only challenges don't need AVG/SUM selection.
  const hasGroupLeaderboard = leaderboards.some(
    (id) => id === ActivityView.GROUP || id === ActivityView.COUNTRY || isDivisionCategoryLeaderboardId(id),
  )
  const showAggregationMethodField = !isLegacyChallenge && hasGroupLeaderboard
  const showDailyTargetField =
    goalType === 'consistentDays' && shouldShowDailyGoal(activityType, isDefinedCustomActivity, simpleManualEntry)
  const showIndexTargetField =
    goalType === 'consistentDays' &&
    (activityType === CustomTypeCategory.ALL || activityType === CustomTypeCategory.ACTIVITY_INDEX)

  const dailyGoalUnit = useDailyGoalUnit(activityType, customType ?? null, customUnit)
  const dailyGoalEndAdornment = useDailyGoalEndAdornment(activityType, customUnit, dailyGoalUnit)

  const aggregationMethodField = (
    <Controller
      name={'avgAgg'}
      //rules={validationRules}
      control={control}
      render={({ field: { ref, onChange, value, ...otherField }, fieldState: { error } }) => (
        <div
          style={{
            marginTop:
              showCustomUnitField || showIndexTargetField || (showDailyTargetField && hasAnyGoalEnabled)
                ? '12px'
                : '24px',
          }}
        >
          <YumSelect
            //label={t`Aggregation method`}
            label={t`Group calculation`}
            error={!!error}
            //InputProps={inputProps}
            startIcon={CollectiveGoalInactiveIcon}
            disabled={isActivityIndex}
            style={{
              margin: 0,
            }}
            {...otherField}
            // Convert boolean to string for display, handle undefined/null case
            value={isActivityIndex ? 'true' : value === true ? 'true' : value === false ? 'false' : ''}
            // Convert string back to boolean on change
            onChange={(e) => {
              if (isActivityIndex) {
                return
              }
              onChange(e.target.value === 'true')
            }}
          >
            <option value={'true'}>{c('short').t`AVG`}</option>
            <option value={'false'}>{c('short').t`SUM`}</option>
          </YumSelect>
        </div>
      )}
    />
  )

  const isLegacyCollectiveGoal = goalType === 'collective'

  // ============================================================================
  // Render
  // ============================================================================

  // If legacy collective goal, add a collective goal
  if (isLegacyCollectiveGoal && isLegacyChallenge && !leaderboards.includes(ActivityView.COLLECTIVE)) {
    leaderboards = [...leaderboards, ActivityView.COLLECTIVE]
  }

  const showIndividualGoal = !isCalories && !isLegacyCollectiveGoal && leaderboards.includes(ActivityView.INDIVIDUAL)
  const showTeamGoal = !isCalories && !isLegacyCollectiveGoal && leaderboards.includes(ActivityView.GROUP)
  const showCountryGoal = !isCalories && !isLegacyCollectiveGoal && leaderboards.includes(ActivityView.COUNTRY)
  const showCollectiveGoal = !isActivityIndex && leaderboards.includes(ActivityView.COLLECTIVE)
  // Some activity/goal type combinations must only show the individual goal section.
  // This keeps the UI consistent with the existing validation logic.
  const showNonIndividualGoals = !isLegacyChallenge // !(isActivityIndex && goalType === 'totalAmount')
  const hasGroupGoal = Object.values(groupGoals).some((goal) => goal?.enabled)
  const hasAnyGoalEnabled =
    !!individualGoal?.enabled ||
    !!teamGoal?.enabled ||
    !!countryGoal?.enabled ||
    !!collectiveGoal?.enabled ||
    hasGroupGoal

  // Build the goal sections in the exact order defined by the leaderboards list.
  // This keeps the admin step aligned with the ordering used elsewhere in the app.
  const orderedGoalSections = leaderboards.map((leaderboardId) => {
    if (leaderboardId === ActivityView.INDIVIDUAL) {
      if (!showIndividualGoal) return null
      return (
        <GoalSection
          key={leaderboardId}
          title={t`Individuals`}
          goal={individualGoal}
          fieldPrefix="goals.individual"
          showAddButton={
            (!isSimpleDayType(activityType as CustomTypeCategory) ||
              activityType !== CustomTypeCategory.TOTAL_POINTS) &&
            (!individualGoal || !individualGoal.enabled)
          }
          onAddGoal={handleAddIndividualGoal}
          goalType={goalType}
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          activityType={activityType}
          formStep={formStep}
          isIndividualGoal={true}
          isLegacyChallenge={isLegacyChallenge}
        />
      )
    }

    if (leaderboardId === ActivityView.GROUP) {
      if (!showNonIndividualGoals || !showTeamGoal) return null
      return (
        <GoalSection
          key={leaderboardId}
          title={t`Teams`}
          goal={teamGoal}
          fieldPrefix="goals.team"
          showAddButton={!teamGoal || !teamGoal.enabled}
          onAddGoal={handleAddTeamGoal}
          goalType={goalType}
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          activityType={activityType}
          formStep={formStep}
          isLegacyChallenge={isLegacyChallenge}
        />
      )
    }

    if (leaderboardId === ActivityView.COUNTRY) {
      if (!showNonIndividualGoals || !showCountryGoal) return null
      return (
        <GoalSection
          key={leaderboardId}
          title={t`Countries`}
          goal={countryGoal}
          fieldPrefix="goals.country"
          showAddButton={!countryGoal || !countryGoal.enabled}
          onAddGoal={handleAddCountryGoal}
          goalType={goalType}
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          activityType={activityType}
          formStep={formStep}
          isLegacyChallenge={isLegacyChallenge}
        />
      )
    }

    if (leaderboardId === ActivityView.COLLECTIVE) {
      if (/*!showNonIndividualGoals ||*/ !showCollectiveGoal) return null
      return (
        <GoalSection
          key={leaderboardId}
          title={t`Collective`}
          goal={collectiveGoal}
          fieldPrefix="goals.collective"
          showAddButton={!collectiveGoal || !collectiveGoal.enabled}
          onAddGoal={handleAddCollectiveGoal}
          goalType={goalType}
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          activityType={activityType}
          formStep={formStep}
          isLegacyChallenge={isLegacyChallenge}
        />
      )
    }

    // Division category leaderboards are stored with a prefix; keep their order.
    if (isDivisionCategoryLeaderboardId(leaderboardId)) {
      if (!showNonIndividualGoals || (isCalories && leaderboardId !== ActivityView.COLLECTIVE)) return null
      const categoryId = getDivisionCategoryIdFromLeaderboardId(leaderboardId)
      if (!categoryId) return null
      const categoryMatch = divisionCategories.find((category) => category.id === categoryId)
      if (!categoryMatch) return null

      const categoryName = translateDivisionCategoryType(categoryMatch)
      const groupGoal = groupGoals[categoryId]
      return (
        <GoalSection
          key={leaderboardId}
          title={categoryName}
          goal={groupGoal}
          fieldPrefix={`goals.group.${categoryId}`}
          showAddButton={!groupGoal || !groupGoal.enabled}
          onAddGoal={() => handleAddGroupGoal(categoryId)}
          goalType={goalType}
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          activityType={activityType}
          formStep={formStep}
          isLegacyChallenge={isLegacyChallenge}
        />
      )
    }

    return null
  })

  return (
    <>
      {/* {activityType === CustomTypeCategory.CUSTOM && simpleManualEntryField} */}
      {showCustomUnitField && (
        <div style={{ marginTop: '24px' }}>
          <CustomUnitField
            control={control}
            fieldPrefix=""
            activityType={activityType}
            formStep={formStep}
            validationRules={validationRules.customType}
          />
        </div>
      )}
      {/* Show the daily target only after at least one goal is enabled. */}
      {showDailyTargetField && hasAnyGoalEnabled ? (
        <div style={{ marginTop: '24px' }}>
          <DailyGoalField
            control={control}
            fieldPrefix=""
            validationRules={consistencyValidationRules.dailyGoal}
            endAdornment={dailyGoalEndAdornment}
          />
        </div>
      ) : null}
      {/* Activity index goal should have a daily target */}
      {showIndexTargetField && (
        <div style={{ marginTop: '24px' }}>
          <IndexTargetField
            control={control}
            fieldPrefix=""
            validationRules={validationRules.indexTarget}
            unitText={dailyGoalUnit}
            isDailyTarget
          />
        </div>
      )}
      {/* Show the aggregation method only if at least one aggregated leaderboard is enabled. */}
      {showAggregationMethodField && aggregationMethodField}
      {/* Goal sections follow the exact order from the leaderboards list. */}
      {orderedGoalSections}
      {/* Information */}
      {isCalories && (
        <div
          style={{ textAlign: 'center', opacity: 0.5, marginTop: '20px' }}
        >{t`Because calorie burn varies between individuals, only collective goals are supported.`}</div>
      )}
      {isLegacyChallenge && (
        <div style={{ textAlign: 'center', opacity: 0.5, marginTop: '20px' }}>
          {t`Tip: Create a new challenge for more goal options.`}
        </div>
      )}
    </>
  )
}
